import AudioRecorder from 'components/AudioRecorder/AudioRecorder';
import { EditAudio, SaveTempAudio, UpdateAudio } from 'components/AudioRecorder/Recorder.api';
import FileSelect from 'components/FileSelect/FileSelect';
import MultiSelect from 'components/MultiSelect/MultiSelect';
import AudioPreview from 'components/UI/AudioPreview';
import { usePerms } from 'contexts/PermsContext';
import useSirenAudio from 'hooks/useSirenAudio';
import { useEffect, useState } from 'react';
import { IoClose, IoPause, IoPlay } from 'react-icons/io5';
import { TbPencil, TbPencilCheck, TbPencilX } from 'react-icons/tb';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { GetDevicesOfType } from 'utils/integrationsUtils';
import config from './AudioManager.config.json';

const MODES = {
  none: 0,
  immediate: 1,
  save: 2,
};

export default function AudioManager({
  deviceIds,
  groupDevices,
  group,
  onClose = () => {},
  onPlayTemp = () => {},
}) {
  const { data: connectedDevices = [] } = useGetConnectedDevicesQuery();
  const perms = usePerms();

  const [deviceObjects, setDeviceObjects] = useState([]);
  const { allAudio } = useSirenAudio(deviceObjects);

  const [mode, setMode] = useState(MODES['none']);
  const [selectedDevices, setSelectedDevices] = useState(deviceIds ? [...deviceIds] : []);
  const [file, setFile] = useState(null);
  const [blob, setBlob] = useState(null);
  const [fileName, setFileName] = useState('');
  const [selectedAudios, setSelectedAudios] = useState([]);
  const [audioList, setAudioList] = useState([]);
  const [errMsg, setErrMsg] = useState('');
  const [toDelete, setToDelete] = useState(null);
  const [toEdit, setToEdit] = useState(null);
  const [editName, setEditName] = useState('');
  const [updateAudioList, setUpdateAudioList] = useState(false);

  useEffect(() => {
    const devicesObjs = connectedDevices?.filter((device) => deviceIds.includes(device.id));

    setDeviceObjects(devicesObjs);
  }, [deviceIds, connectedDevices]);

  useEffect(() => {
    const uniqueAudios = allAudio.filter((audio) => !!audio.device_ids);
    setAudioList(uniqueAudios);
  }, [allAudio]);

  const getDeviceOptions = () => {
    const deviceList = GetDevicesOfType(['Technology Bridge_Siren/Giant Voice'], connectedDevices);

    return deviceList
      .filter((device) => device?.camera)
      .map((device) => {
        return {
          name: device.alias,
          value: device.id,
          forceSelect: group,
        };
      });
  };

  const updateFile = (newFile) => {
    setBlob(null);
    setFile(newFile);
  };

  const updateBlob = (newBlob, fileName) => {
    if (newBlob && file) setFile(null);

    setBlob(newBlob);
    setFileName(fileName);
  };

  const onDone = async () => {
    if (!selectedAudios || selectedAudios.length < 1) {
      onClose();
      return;
    }

    try {
      const audioToUpdate = Array.isArray(selectedAudios) ? selectedAudios[0] : selectedAudios;
      let devicesToUpdate = group ? [...deviceIds] : [...selectedDevices];

      //TODO: Implement a method to remove group from association

      //make sure to include devices that are already associated with audio stay
      if (group) {
        const audioFound = audioList.find((audio) => audio.id === audioToUpdate);

        if (audioFound) {
          devicesToUpdate = [...new Set([...devicesToUpdate, ...audioFound.device_ids])];
        }
      }

      const updateRes = await UpdateAudio(audioToUpdate, devicesToUpdate);

      if (updateRes?.status === 200) {
        //deviceData?.updateDevices();
        onClose();
      }
    } catch (e) {
      console.error('Error in AudioManager:onDone:', e);
      setErrMsg(e.message);
    }
  };

  const onSaveTemp = async (toDelete, override) => {
    setErrMsg('');

    try {
      const res = await SaveTempAudio(toDelete || false, override);

      if (res?.status === 200) {
        //deviceData?.updateDevices();
        setUpdateAudioList((prev) => !prev);
      }
    } catch (e) {
      console.error('Error in AudioManager:onSaveTemp:', e);
      setErrMsg(e.message);
    }
  };

  const onPlayTempInt = () => {
    // onSaveTemp(true);
    onPlayTemp(fileName);
  };

  const onEdit = (audio) => {
    setToEdit(audio.id);
    setEditName(audio.name);
  };

  const onResolveEdit = async () => {
    if (!toEdit || !editName) return;

    setErrMsg('');

    try {
      const res = await EditAudio(toEdit, editName);

      if (res?.status === 200) {
        //deviceData?.updateDevices();
        setToEdit(null);
        setEditName('');
      }
    } catch (error) {
      console.error('Error in AudioManager:onResolveEdit:', error);
      setErrMsg(error.message);
    }
  };

  const onCancelEdit = () => {
    setEditName('');
    setToEdit(null);
  };

  const onAudioSelectToggle = (e, audioId) => {
    if (e.target.checked) {
      const audioFound = audioList.find((audio) => audio.id === audioId);
      setSelectedAudios([audioId]);

      if (!group) setSelectedDevices([...audioFound.device_ids]);
    } else {
      setSelectedAudios([]);
    }
  };

  if (!deviceIds || deviceIds?.length === 0) return null;

  return (
    <div className="container-fluid flex-grow-1 h-100 d-flex flex-column">
      <div className="d-flex flex-column flex-md-row justify-content-between">
        <div>
          <h3>Select the file you want to use</h3>
        </div>

        <div>
          <p className="m-0">(Supported file type(s): .mp3)</p>
          <p className="m-0">(Max file size: 1.5 MB per file)</p>
        </div>
      </div>

      <div className="flex-fill d-flex flex-column gap-3 py-2">
        <div className="flex-fill h-100 d-flex flex-column flex-sm-row gap-3">
          <div className="flex-fill w-100 bg-primary">
            <FileSelect
              file={file}
              fileTypes={['.mp3', '.au', '.opus', '.vorbis', '.wav']}
              onChange={updateFile}
              maxFileSize={1500000}
              onSave={onSaveTemp}
            />
          </div>
          <div className="flex-fill w-100 bg-primary d-flex">
            <AudioRecorder
              usedNames={audioList.map((audio) => audio.displayName)}
              maxDuration={config['max-record-duration-ms']}
              onChange={updateBlob}
              clear={blob === null}
              onPlay={onPlayTempInt}
              onSave={onSaveTemp}
            />
          </div>
        </div>

        <div
          className="flex-fill d-flex flex-column h-100 overflow-hidden bg-primary"
          style={{ minHeight: '150px' }}
        >
          <div className="text-center">
            <h4>Select from pre-existing files</h4>
            {audioList?.length === 0 && <p>No pre-existing files uploaded.</p>}
          </div>

          <div className="position-relative flex-fill h-100">
            <div className="position-absolute align-items-stretch top-0 bottom-0 start-0 end-0 overflow-y-auto">
              {[...audioList].map((audio, index) => (
                <div
                  key={index}
                  className={
                    'container-fluid row ' + (index % 2 ? 'bg-primary-shadow' : 'bg-primary')
                  }
                >
                  {perms.testPermission([], ['Edit-DeleteSirenAudioFiles']) && (
                    <div className="col-1">
                      <input
                        className="mt-2"
                        type="checkbox"
                        checked={selectedAudios.includes(audio.id)}
                        onChange={(e) => onAudioSelectToggle(e, audio.id)}
                      />
                    </div>
                  )}

                  <div className="col-10 col-sm-4">
                    {toEdit !== audio.id && (
                      <p className="my-2">
                        {audio.displayName} ({(audio?.size / 1048576).toFixed(2)} MB)
                      </p>
                    )}

                    {toEdit === audio.id && (
                      <input
                        className="form-control bg-primary"
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                      />
                    )}
                  </div>

                  <div className="col-3 col-sm-4">
                    <AudioPreview
                      tone={audio}
                      playButtonOverride={
                        <button className="btn">
                          <IoPlay size={20} />
                        </button>
                      }
                      pauseButtonOverride={
                        <button className="btn">
                          <IoPause size={20} />
                        </button>
                      }
                      update={updateAudioList}
                    />
                  </div>

                  {perms.testPermission([], ['Edit-DeleteSirenAudioFiles']) && (
                    <div className="col-5 col-sm-2">
                      <div className="d-flex">
                        {toEdit !== audio.id && (
                          <button className="btn" onClick={() => onEdit(audio)}>
                            <TbPencil size={20} />
                          </button>
                        )}
                        {toEdit === audio.id && (
                          <button className="btn" onClick={onResolveEdit}>
                            <TbPencilCheck size={20} />
                          </button>
                        )}
                        {toEdit === audio.id && (
                          <button className="btn" onClick={onCancelEdit}>
                            <TbPencilX size={20} />
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {perms.testPermission([], ['Edit-DeleteSirenAudioFiles']) && (
                    <div className="col-4 col-sm-1">
                      <button className="btn" onClick={() => setToDelete(audio.id)}>
                        <IoClose size={25} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex-fill h-100 bg-primary" style={{ minHeight: '150px' }}>
          <h3>Choose file association(s)</h3>

          <div>
            <MultiSelect
              options={getDeviceOptions()}
              value={selectedDevices}
              onChange={!group ? (v) => setSelectedDevices(v) : () => {}}
            />
          </div>
        </div>

        <div className="d-flex flex-row justify-content-between">
          <button className="btn bg-black border-white p-3" onClick={onClose}>
            Cancel
          </button>

          <div />

          {errMsg && <p className="text-danger">{errMsg}</p>}

          {mode === MODES['immediate'] && (
            <p>Audio will play immediately and be discarded afterwards.</p>
          )}

          <button className="btn btn-primary p-3" onClick={onDone}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
